<template>
  <div class="jss" @focusin="onFocusIn" @focusout="onFocusOut">
    <multiselect
      :id="elementId"
      ref="ms"
      :value="null"
      :options="mergedOptions"
      :loading="loading"
      :internal-search="false"
      :searchable="active"
      :allow-empty="true"
      :disabled="disabled"
      :placeholder="placeholder"
      track-by="id"
      :custom-label="labelFor"
      select-label=""
      selected-label=""
      deselect-label=""
      @select="onSelect"
      @search-change="onSearch"
      @open="onOpen"
      @close="onClose"
    >
      <template slot="noResult">
        <span v-if="!loading">No judges found.</span>
      </template>
      <template slot="noOptions">Start typing to search...</template>
      <li v-if="loadingMore" slot="afterList" class="multiselect__element">
        <span class="multiselect__option text-muted">Loading more...</span>
      </li>
    </multiselect>

    <!-- Same chip markup as HorseSearchSelect/MemberSearchSelect (styled in _filter.scss /
         _badges.scss) — one per selected judge, multi mode can hold several. -->
    <div v-if="selectedOptions.length" class="tags-input mt-1 d-flex flex-wrap">
      <span class="badge mr-1 mb-1" v-for="o in selectedOptions" :key="o.id">
        <button
          v-if="!disabled"
          type="button"
          class="tags-input-remove"
          aria-label="Remove"
          @mousedown.prevent
          @click.stop.prevent="remove(o.id)"
        />
        <span class="tags-input-label">{{ labelFor(o) }}</span>
      </span>
    </div>
  </div>
</template>

<script>
import Multiselect from '@/components/AppMultiselect'
import judgesApi from '@/services/api/judges'

// Server-backed replacement for the legacy `<tags-input :existing-tags="{id: 'member_number: name'}">`
// event Judge picker (CAM-1477 follow-up). Structural clone of MemberSearchSelect.vue (same
// freeze-prevention checklist, same chip-below-field layout instead of vue-multiselect's own
// inline tags) — kept as its own file rather than parameterising MemberSearchSelect further,
// since the backing resource (Judge, not User) and endpoint differ. `value` / `input` keep the
// tags-input shape: an array of id strings.
const MIN_CHARS = 2
const DEBOUNCE_MS = 300

export default {
  name: 'JudgeSearchSelect',
  components: { Multiselect },
  props: {
    value: { type: Array, default: () => [] },
    elementId: { type: String, required: true },
    multiple: { type: Boolean, default: false },
    // Pre-resolved [{id, label}] for the currently selected judge(s), supplied by the parent's
    // main request (event details already embeds `judges`). When every id in `value` is covered
    // here, no /v2/judges/options hydration call is fired.
    initialOptions: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Start typing...' },
    disabled: Boolean,
    required: Boolean
  },
  data() {
    return {
      searchOptions: [],
      selectedCache: [],
      // Plain data (rebuilt via watchers), NOT a computed — see MemberSearchSelect.vue for why.
      mergedOptions: [],
      // The searchable <input> only renders while THIS field is focused — see the focus-pinging
      // freeze documented on HorseSearchSelect.vue.
      active: false,
      loading: false,
      loadingMore: false,
      nextCursor: null,
      term: '',
      searchTimer: null,
      scrollEl: null,
      onScroll: null
    }
  },
  computed: {
    selectedIds() {
      return (this.value || [])
        .map(v => Number(v))
        .filter(id => Number.isInteger(id) && id > 0)
    },
    selectedOptions() {
      return this.selectedIds
        .map(id => this.mergedOptions.find(o => o.id === id))
        .filter(Boolean)
    }
  },
  watch: {
    // Watch the resolved id list, not the `value` array prop directly — see MemberSearchSelect.
    selectedIds: {
      immediate: true,
      handler(ids, oldIds) {
        if (oldIds && ids.join(',') === oldIds.join(',')) return
        this.hydrateSelected()
        this.rebuildOptions()
      }
    },
    selectedCache() { this.rebuildOptions() },
    searchOptions() { this.rebuildOptions() }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
    this.detachScroll()
  },
  methods: {
    labelFor(o) {
      return o ? o.label : ''
    },
    extract(res) {
      const d = (res && res.data && res.data.data) || {}
      return { items: d.items || [], nextCursor: d.next_cursor || null }
    },
    rebuildOptions() {
      const seen = new Set(this.searchOptions.map(o => o.id))
      this.mergedOptions = [...this.selectedCache.filter(o => !seen.has(o.id)), ...this.searchOptions]
    },
    hydrateSelected() {
      const known = new Set([
        ...this.selectedCache.map(o => o.id),
        ...this.searchOptions.map(o => o.id),
        ...this.initialOptions.map(o => Number(o.id))
      ])
      const missing = this.selectedIds.filter(id => !known.has(id))

      const fromParent = this.initialOptions
        .filter(o => this.selectedIds.includes(Number(o.id)))
        .map(o => ({ ...o, id: Number(o.id) }))
      this.selectedCache = [
        ...this.selectedCache.filter(o => !fromParent.some(p => p.id === o.id)),
        ...fromParent
      ]

      const toFetch = missing.filter(id => !fromParent.some(p => p.id === id) && Number.isInteger(id) && id > 0)
      if (toFetch.length === 0) return

      judgesApi.options({ ids: toFetch.join(',') })
        .then((res) => {
          const fetched = this.extract(res).items
          const seen = new Set(this.selectedCache.map(o => o.id))
          this.selectedCache = [...this.selectedCache, ...fetched.filter(o => !seen.has(o.id))]
        })
        .catch(() => {})
    },
    onSelect(option) {
      if (!option || option.id == null) return
      if (this.multiple) {
        if (this.selectedIds.includes(option.id)) {
          this.resetMultiselect()
          return
        }
        this.selectedCache = [...this.selectedCache.filter(o => o.id !== option.id), option]
        this.$emit('input', [...this.value.map(String), String(option.id)])
      } else {
        this.selectedCache = [option]
        this.$emit('input', [String(option.id)])
      }
      this.resetMultiselect()
    },
    resetMultiselect() {
      const ms = this.$refs.ms
      if (!ms) return
      ms.internalValue = []
      ms.search = ''
    },
    remove(id) {
      if (this.disabled) return
      this.$emit('input', this.selectedIds.filter(i => i !== id).map(String))
    },
    onSearch(query) {
      this.term = query || ''
      clearTimeout(this.searchTimer)
      if (this.term.length < MIN_CHARS) {
        this.searchOptions = []
        this.nextCursor = null
        return
      }
      this.searchTimer = setTimeout(() => this.runSearch(), DEBOUNCE_MS)
    },
    runSearch() {
      this.loading = true
      judgesApi.options({ search: this.term })
        .then((res) => {
          const { items, nextCursor } = this.extract(res)
          this.searchOptions = items
          this.nextCursor = nextCursor
        })
        .catch(() => { this.searchOptions = [] })
        .finally(() => { this.loading = false })
    },
    loadMore() {
      if (!this.nextCursor || this.loadingMore || this.loading) return
      this.loadingMore = true
      judgesApi.options({ search: this.term, cursor: this.nextCursor })
        .then((res) => {
          const { items, nextCursor } = this.extract(res)
          this.searchOptions = [...this.searchOptions, ...items]
          this.nextCursor = nextCursor
        })
        .catch(() => {})
        .finally(() => { this.loadingMore = false })
    },
    onFocusIn(e) {
      if (this.active || this.disabled) return
      if (e && e.target && e.target.closest && e.target.closest('.tags-input')) return
      this.active = true
      this.$nextTick(() => {
        const ms = this.$refs.ms
        if (!ms) return
        if (!ms.isOpen) ms.activate()
        const input = ms.$refs && ms.$refs.search
        if (input && document.activeElement !== input) input.focus()
      })
    },
    onFocusOut(e) {
      if (e && e.relatedTarget && this.$el.contains(e.relatedTarget)) return
      this.active = false
    },
    onOpen() {
      this.$nextTick(this.attachScroll)
    },
    onClose() {
      this.detachScroll()
    },
    attachScroll() {
      const root = this.$refs.ms && this.$refs.ms.$el
      const wrapper = root && root.querySelector('.multiselect__content-wrapper')
      if (!wrapper) return
      this.scrollEl = wrapper
      this.onScroll = () => {
        if (wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight < 40) this.loadMore()
      }
      wrapper.addEventListener('scroll', this.onScroll)
    },
    detachScroll() {
      if (this.scrollEl && this.onScroll) this.scrollEl.removeEventListener('scroll', this.onScroll)
      this.scrollEl = null
      this.onScroll = null
    }
  }
}
</script>

<style scoped>
.tags-input-remove {
  padding: 0;
  border: 0;
  background-color: transparent;
  position: relative;
  z-index: 2;
  cursor: pointer;
}

.tags-input .badge {
  max-width: 100%;
  min-width: 0;
  align-items: flex-start;
}

.tags-input-label {
  white-space: normal;
  overflow-wrap: break-word;
  min-width: 0;
  text-align: left;
}
</style>



// WEBPACK FOOTER //
// src/components/Judges/JudgeSearchSelect.vue